import { filterObjectNonNull } from './helpers';
import { ChartType, ScenarioType } from 'types/types';

/**
 * Generate an object with a key 'requestParams' which another object holding all
 * the key value pairs required for the filter api call
 * @param {Number} startMonth
 * @param {Number} endMonth
 * @param {Number} zone
 * @param {Number} market
 * @param {Number} team
 * @param {Number} rm
 */
export const generateRequestParamsForFilterApi = (
    startMonth,
    endMonth,
    zone,
    market,
    team,
    rm
) => {
    return {
        requestParams: filterObjectNonNull({
            rm_zone: zone,
            rm_region: market,
            rm_group: team,
            rm,
            start: startMonth,
            end: endMonth
        })
    };
};

/**
 * Generate an object, omitting any null value, holding all the key value pairs required for a landing api call
 * @param {Number} zone
 * @param {Number} market
 * @param {Number} team
 * @param {Number} rm
 * @param {Number} startMonth
 * @param {Number} endMonth
 */
export const generateRequestParamsForLandingApi = (
    zone,
    market,
    team,
    rm,
    startMonth,
    endMonth
) => {
    return filterObjectNonNull({
        rm_zone: zone,
        rm_region: market,
        rm_group: team,
        rm: rm,
        start: startMonth,
        end: endMonth
    });
};

/**
 * Generate an object with a key 'requestParams' which another object holding all
 * the key value pairs required for the main stats api call
 * @param {Object} params params object containing request params
 * @param {Number} params.startMonth
 * @param {Number} params.endMonth
 * @param {Number} params.market
 * @param {Number} params.team
 * @param {Number} params.rm
 */
export const generateRequestParamsForMainStatsApi = (params) => {
    const { market, team, rm, startMonth, endMonth } = params;
    return {
        requestParams: filterObjectNonNull({
            rm_region: market,
            rm_group: team,
            rm,
            start: startMonth,
            end: endMonth
        })
    };
};

/**
 * Generate an object, omitting any null value, holding all the key value pairs required for a rm api call
 * @param {Number} zone
 * @param {Number} market
 * @param {Number} team
 * @param {Number} rm
 * @param {Number} startMonth
 * @param {Number} endMonth
 */
export const generateRequestParamsForRmApi = (
    zone,
    market,
    team,
    rm,
    startMonth,
    endMonth
) => {
    return filterObjectNonNull({
        rm_zone: zone,
        rm_region: market,
        rm_group: team,
        rm: rm,
        start: startMonth,
        end: endMonth
    });
};

/**
 * Generate an object with a key 'requestParams' which another object holding all
 * the key value pairs required for the scenario api call based on ChartType enum.
 * Returns null when ChartType is not found.
 * @param {String} type ChartType enum
 * @param {String} scenario ScenarioType enum
 * @param {Number} market the numerical value of market
 * @param {Number} team the numerical value of team
 * @param {Number} rm the numerical value of rm
 * @param {Number} startMonth the epoch numerical value of start date
 * @param {Number} endMonth the epoch numerical value of end date
 * @param {Object} allValues All values of market, team, rm
 * @param {Number} allValues.market market info of 'All'
 * @param {Number} allValues.market.value market numerical value of 'All'
 * @param {String} allValues.market.label 'All' label
 * @param {Number} allValues.team team info of 'All'
 * @param {Number} allValues.team.value team numerical value of 'All'
 * @param {String} allValues.team.label 'All' label
 * @param {Number} allValues.rm rm info of 'All'
 * @param {Number} allValues.rm.value rm numerical value of 'All'
 * @param {String} allValues.rm.label 'All' label
 * @param {String} zone 'Zone' label
 * @param {String} ctoCode CTO Code
 */
export const generateRequestParamsForScenarioApi = (
    type,
    scenario,
    market,
    team,
    rm,
    startMonth,
    endMonth,
    allValues,
    zone,
    ctoCode
) => {
    switch (type) {
        case ChartType.Outliers:
            return {
                scenario,
                requestParams: filterObjectNonNull({
                    rm_region: market,
                    rm_group: team,
                    rm: rm === allValues.rm.value ? null : rm,
                    start: startMonth,
                    end: endMonth,
                    rm_zone: zone
                })
            };
        case ChartType.VC:
            return {
                scenario,
                requestParams: filterObjectNonNull({
                    rm_region: market,
                    rm_group: team,
                    rm,
                    start: startMonth,
                    end: endMonth
                })
            };
        case ChartType.BreachCategory:
            return {
                scenario,
                requestParams: filterObjectNonNull({
                    rm_region: market,
                    rm_group: team,
                    rm,
                    start: startMonth,
                    end: endMonth
                })
            };
        case ChartType.Trend:
            return {
                scenario,
                requestParams: filterObjectNonNull({
                    rm_region: market,
                    rm_group: team,
                    rm,
                    start: startMonth,
                    end: endMonth
                })
            };
        case ChartType.PortfolioBox:
            return {
                scenario,
                requestParams: filterObjectNonNull({
                    start: startMonth,
                    end: endMonth,
                    ctoCode
                })
            };
        case ScenarioType.Stats:
            return {
                scenario,
                requestParams: filterObjectNonNull({
                    rm_region: market,
                    rm_group: team,
                    rm,
                    start: startMonth,
                    end: endMonth
                })
            };
        case ScenarioType.TopFiveRms:
            return {
                scenario,
                requestParams: filterObjectNonNull({
                    rm_region: market,
                    rm_group: team,
                    rm,
                    start: startMonth,
                    end: endMonth
                })
            };
        case ScenarioType.DetailView:
            return {
                scenario,
                requestParams: filterObjectNonNull({
                    rm_region:
                        market === allValues.market.value ? null : market,
                    rm_group: team === allValues.team.value ? null : team,
                    rm: rm === allValues.rm.value ? null : rm,
                    start: startMonth,
                    end: endMonth
                })
            };
        default:
            return null;
    }
};

/**
 * Generate an object with a key 'requestParams' which another object holding all
 * the key value pairs required for the Market view filter api call
 * @param {Number} startMonth
 * @param {Number} endMonth
 * @param {Number} zone
 * @param {Number} team
 */
export const generateRequestParamsForMarketViewFilterApi = (
    startMonth,
    endMonth,
    zone,
    team
) => {
    return {
        requestParams: filterObjectNonNull({
            rm_zone: zone,
            rm_region: null,
            rm_group: team,
            rm: null,
            start: startMonth,
            end: endMonth
        })
    };
};

/**
 * Generate an object, omitting any null value, holding all the key value pairs required for a Market view api call
 * @param {Number} zone
 * @param {Number} market
 * @param {Number} team
 * @param {Number} rm
 * @param {Number} startMonth
 * @param {Number} endMonth
 */
export const generateRequestParamsForMarketViewApi = (
    zone,
    market,
    team,
    rm,
    startMonth,
    endMonth
) => {
    return filterObjectNonNull({
        rm_zone: zone,
        rm_region: market,
        rm_group: team,
        rm,
        start: startMonth,
        end: endMonth
    });
};

/**
 * Generate an object with a key 'requestParams' which another object holding all
 * the key value pairs required for the Team view filter api call
 * @param {Number} startMonth
 * @param {Number} endMonth
 * @param {Number} zone
 * @param {Number} rm
 */
export const generateRequestParamsForTeamViewFilterApi = (
    startMonth,
    endMonth,
    zone,
    rm
) => {
    return {
        requestParams: filterObjectNonNull({
            rm_zone: zone,
            rm_region: null,
            rm_group: null,
            rm,
            start: startMonth,
            end: endMonth
        })
    };
};

/**
 * Generate an object, omitting any null value, holding all the key value pairs required for a Team view api call
 * @param {Number} zone
 * @param {Number} market
 * @param {Number} team
 * @param {Number} rm
 * @param {Number} startMonth
 * @param {Number} endMonth
 */
export const generateRequestParamsForTeamViewApi = (
    zone,
    market,
    team,
    rm,
    startMonth,
    endMonth
) => {
    return filterObjectNonNull({
        rm_zone: zone,
        rm_region: market,
        rm_group: team,
        rm: rm,
        start: startMonth,
        end: endMonth
    });
};
